import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Brain, Upload, FileText, CheckCircle2, AlertCircle,
  Loader2, Database, Scale, TrendingUp, Settings,
  ChevronDown, X, Sparkles, Globe, Crown
} from 'lucide-react';

const BRAIN_INGEST_URL = '/webhook/brain-ingest';
const BRAIN_UPLOAD_URL = '/webhook/brain-upload';
const QDRANT_BASE_URL  = '/api/qdrant/collections';

type Mode = 'ceo' | 'kanzlei';

interface BrainUploadProps {
  mode?: Mode;
  kanzleiId?: string;
  kanzleiName?: string;
}

interface CollectionInfo {
  name: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  ceOnly?: boolean;
}

interface UploadEntry {
  id: string;
  title: string;
  collection: string;
  timestamp: string;
  status: 'ok' | 'error';
  chunks?: number;
}

const COLLECTIONS: CollectionInfo[] = [
  {
    name: 'recht',
    label: 'Recht & Judikatur',
    icon: <Scale className="w-4 h-4" />,
    color: 'blue',
    description: 'OGH-Urteile, Gesetze, Rechtsnormen – täglich automatisch befüllt via RIS'
  },
  {
    name: 'sales',
    label: 'Sales & Pitches',
    icon: <TrendingUp className="w-4 h-4" />,
    color: 'emerald',
    description: 'Einwände, Gesprächsleitfäden, Abschluss-Strategien',
    ceOnly: true
  },
  {
    name: 'prozesse',
    label: 'Prozesse & SOPs',
    icon: <Settings className="w-4 h-4" />,
    color: 'purple',
    description: 'Interne Abläufe, Handbücher, Onboarding-Dokumente',
    ceOnly: true
  },
  {
    name: 'markt',
    label: 'Markt & Konkurrenz',
    icon: <Globe className="w-4 h-4" />,
    color: 'orange',
    description: 'Marktdaten, Konkurrenz-Analysen, Branchentrends',
    ceOnly: true
  }
];

const colorMap: Record<string, string> = {
  blue:    'border-blue-500/40 bg-blue-900/10 text-blue-400',
  emerald: 'border-emerald-500/40 bg-emerald-900/10 text-emerald-400',
  purple:  'border-purple-500/40 bg-purple-900/10 text-purple-400',
  orange:  'border-orange-500/40 bg-orange-900/10 text-orange-400',
};

const BrainUpload: React.FC<BrainUploadProps> = ({
  mode = 'ceo',
  kanzleiId,
  kanzleiName = 'Ihre Kanzlei'
}) => {
  const isCeo = mode === 'ceo';

  const [selectedCollection, setSelectedCollection] = useState(isCeo ? 'recht' : 'recht');
  const [title, setTitle]         = useState('');
  const [pastedText, setPastedText] = useState('');
  const [source, setSource]       = useState(isCeo ? 'CEO-Dashboard' : kanzleiName);
  const [dragOver, setDragOver]   = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult]       = useState<{ ok: boolean; message: string } | null>(null);
  const [history, setHistory]     = useState<UploadEntry[]>(() => {
    try { return JSON.parse(localStorage.getItem('brain_upload_history') || '[]'); }
    catch { return []; }
  });
  const [collectionStats, setCollectionStats] = useState<Record<string, number>>({});
  const [statsLoading, setStatsLoading]       = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Qdrant Collection-Größen direkt abfragen
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      const collections = isCeo
        ? ['recht', 'sales', 'prozesse', 'markt']
        : [`recht_${kanzleiId}`, 'recht'];
      const stats: Record<string, number> = {};
      await Promise.all(
        collections.map(async (col) => {
          try {
            const r = await fetch(`${QDRANT_BASE_URL}/${col}`);
            if (r.ok) {
              const d = await r.json();
              stats[col] = d.result?.points_count ?? 0;
            } else {
              stats[col] = 0;
            }
          } catch {
            stats[col] = 0;
          }
        })
      );
      setCollectionStats(stats);
      setStatsLoading(false);
    };
    fetchStats();
  }, [isCeo, kanzleiId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  }, []);

  const readFileAsText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file, 'utf-8');
    });

  const handleUpload = async () => {
    if (!title.trim()) { setResult({ ok: false, message: 'Bitte einen Titel eingeben.' }); return; }
    if (!pastedText.trim() && !selectedFile) {
      setResult({ ok: false, message: 'Bitte Text eingeben oder eine Datei hochladen.' });
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      let text = pastedText.trim();

      if (selectedFile && !text) {
        const name = selectedFile.name.toLowerCase();
        if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.json')) {
          text = await readFileAsText(selectedFile);
        } else if (name.endsWith('.pdf')) {
          // PDF: als FormData an brain-upload schicken
          const fd = new FormData();
          fd.append('file', selectedFile);
          fd.append('title', title);
          fd.append('collection', isCeo ? selectedCollection : 'recht');
          fd.append('source', source);
          if (!isCeo && kanzleiId) fd.append('kanzleiId', kanzleiId);

          const r = await fetch(BRAIN_UPLOAD_URL, { method: 'POST', body: fd });
          const data = await r.json();

          const entry: UploadEntry = {
            id: Date.now().toString(),
            title,
            collection: isCeo ? selectedCollection : `recht_${kanzleiId}`,
            timestamp: new Date().toLocaleString('de-AT'),
            status: r.ok ? 'ok' : 'error',
            chunks: data.chunks_ingested
          };
          const newHistory = [entry, ...history].slice(0, 20);
          setHistory(newHistory);
          localStorage.setItem('brain_upload_history', JSON.stringify(newHistory));
          setResult({ ok: r.ok, message: r.ok ? `PDF gespeichert! ${data.chunks_ingested ?? '?'} Chunks im BRAIN.` : 'Fehler beim PDF-Upload.' });
          setUploading(false);
          return;
        } else {
          setResult({ ok: false, message: 'Unterstützte Formate: PDF, TXT, MD, JSON' });
          setUploading(false);
          return;
        }
      }

      // Text-Ingest über JSON-Webhook
      const targetCollection = isCeo
        ? selectedCollection
        : kanzleiId ? `recht_${kanzleiId}` : 'recht';

      const payload = {
        collection: isCeo ? selectedCollection : 'recht',
        title: title.trim(),
        text,
        source,
        type: 'Upload',
        ...(kanzleiId && !isCeo ? { kanzleiId } : {})
      };

      const r = await fetch(BRAIN_INGEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await r.json();
      const entry: UploadEntry = {
        id: Date.now().toString(),
        title,
        collection: targetCollection,
        timestamp: new Date().toLocaleString('de-AT'),
        status: r.ok ? 'ok' : 'error',
        chunks: data.chunks_ingested
      };
      const newHistory = [entry, ...history].slice(0, 20);
      setHistory(newHistory);
      localStorage.setItem('brain_upload_history', JSON.stringify(newHistory));

      setResult({
        ok: r.ok,
        message: r.ok
          ? `Gespeichert! ${data.chunks_ingested ?? '?'} Chunks im BRAIN – ab sofort für alle ${isCeo ? 'Kunden' : 'deine Anfragen'} aktiv.`
          : 'Fehler: ' + (data.message || 'Unbekannter Fehler.')
      });

      if (r.ok) {
        setTitle('');
        setPastedText('');
        setSelectedFile(null);
      }
    } catch (e: any) {
      setResult({ ok: false, message: 'Verbindungsfehler: ' + e.message });
    } finally {
      setUploading(false);
    }
  };

  const availableCollections = isCeo
    ? COLLECTIONS
    : COLLECTIONS.filter(c => !c.ceOnly);

  return (
    <div className="animate-fade-in space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${isCeo ? 'bg-gradient-to-br from-blue-600 to-purple-700' : 'bg-gradient-to-br from-emerald-600 to-blue-700'}`}>
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">ARKORBIT BRAIN</h1>
            <p className="text-sm text-slate-400">
              {isCeo ? 'CEO Wissensdatenbank – alle Collections verwalten' : `Premium Wissensdatenbank – ${kanzleiName}`}
            </p>
          </div>
        </div>
        {!isCeo && (
          <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            <Crown className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-bold">PREMIUM</span>
          </div>
        )}
      </div>

      {/* Collection Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(isCeo ? COLLECTIONS : COLLECTIONS.filter(c => c.name === 'recht')).map(col => (
          <button
            key={col.name}
            onClick={() => isCeo && setSelectedCollection(col.name)}
            disabled={!isCeo}
            className={`p-4 rounded-xl border transition-all text-left ${
              selectedCollection === col.name && isCeo
                ? `${colorMap[col.color]} ring-2 ring-blue-500/50`
                : 'border-slate-700/50 bg-slate-800/30 hover:bg-slate-800/60'
            } ${isCeo ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className={`flex items-center gap-2 mb-2 ${selectedCollection === col.name && isCeo ? '' : 'text-slate-400'}`}>
              {col.icon}
              <span className="text-xs font-bold uppercase tracking-wider">{col.label}</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : (collectionStats[col.name] ?? '–')}
            </div>
            <div className="text-xs text-slate-500 mt-1">Einträge</div>
          </button>
        ))}
        {!isCeo && kanzleiId && (
          <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-900/10 text-left">
            <div className="flex items-center gap-2 mb-2 text-emerald-400">
              <Scale className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-wider">Ihre Urteile</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {statsLoading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : (collectionStats[`recht_${kanzleiId}`] ?? 0)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Eigene Einträge</div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">

        {/* Upload Panel */}
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-400" />
            {isCeo ? 'Wissen einspielen' : 'Eigenes Urteil hochladen'}
          </h2>

          {/* Collection Auswahl (nur CEO) */}
          {isCeo && (
            <div>
              <label className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2 block">Collection</label>
              <div className="relative">
                <select
                  value={selectedCollection}
                  onChange={e => setSelectedCollection(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white appearance-none pr-10 focus:outline-none focus:border-blue-500"
                >
                  {COLLECTIONS.map(c => (
                    <option key={c.name} value={c.name}>{c.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                {COLLECTIONS.find(c => c.name === selectedCollection)?.description}
              </p>
            </div>
          )}

          {/* Titel */}
          <div>
            <label className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2 block">
              Titel / Bezeichnung *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={isCeo ? 'z.B. OGH 5Ob200/24 – Mietrecht Kündigung' : 'z.B. OGH 3Ob120/24g'}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Datei Drag & Drop */}
          <div>
            <label className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2 block">
              Datei hochladen (PDF, TXT, MD)
            </label>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-blue-500 bg-blue-500/10'
                  : selectedFile
                  ? 'border-emerald-500/50 bg-emerald-500/5'
                  : 'border-slate-600 hover:border-slate-400 bg-slate-800/30'
              }`}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium text-sm truncate max-w-[200px]">{selectedFile.name}</span>
                  <button onClick={e => { e.stopPropagation(); setSelectedFile(null); }}>
                    <X className="w-4 h-4 text-slate-400 hover:text-white" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">Datei hierher ziehen oder klicken</p>
                  <p className="text-slate-600 text-xs mt-1">PDF, TXT, MD – max. 50 MB</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.md,.json"
              className="hidden"
              onChange={e => e.target.files?.[0] && setSelectedFile(e.target.files[0])}
            />
          </div>

          {/* ODER Text einfügen */}
          <div>
            <label className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2 block">
              Text direkt einfügen
            </label>
            <textarea
              value={pastedText}
              onChange={e => setPastedText(e.target.value)}
              placeholder={
                isCeo
                  ? 'Urteilstext, Gesetzestext, Fachwissen...'
                  : 'Urteilstext aus RIS kopieren und hier einfügen...'
              }
              rows={5}
              className="w-full bg-slate-800 border border-slate-600 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 text-sm resize-none"
            />
            {pastedText && (
              <p className="text-xs text-slate-500 mt-1">{pastedText.length.toLocaleString()} Zeichen</p>
            )}
          </div>

          {/* Feedback */}
          {result && (
            <div className={`flex items-start gap-3 p-4 rounded-xl border ${
              result.ok
                ? 'bg-emerald-900/20 border-emerald-500/30 text-emerald-300'
                : 'bg-red-900/20 border-red-500/30 text-red-300'
            }`}>
              {result.ok
                ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              }
              <p className="text-sm">{result.message}</p>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 shadow-lg hover:shadow-blue-500/20"
          >
            {uploading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Wird verarbeitet...</>
            ) : (
              <><Sparkles className="w-5 h-5" /> In BRAIN einlesen</>
            )}
          </button>

          {!isCeo && (
            <p className="text-xs text-slate-500 text-center">
              Urteile aus dem RIS: <a href="https://ris.bka.gv.at/Judikatur/" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">ris.bka.gv.at/Judikatur</a>
            </p>
          )}
        </div>

        {/* Anleitung + History */}
        <div className="space-y-4">

          {/* Anleitung */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <Database className="w-4 h-4 text-purple-400" />
              {isCeo ? 'Wie es funktioniert' : 'So laden Sie Ihre Urteile hoch'}
            </h3>
            <ol className="space-y-3">
              {isCeo ? (
                <>
                  <Step n={1} text="Collection wählen (Recht, Sales, Prozesse oder Markt)" />
                  <Step n={2} text="PDF hochladen oder Text direkt einfügen" />
                  <Step n={3} text="Titel eingeben und auf 'In BRAIN einlesen' klicken" />
                  <Step n={4} text="Das System chunkt, embeddet und speichert in Qdrant" />
                  <Step n={5} text="Jeder neue Anwalt-Aufruf profitiert sofort davon" />
                </>
              ) : (
                <>
                  <Step n={1} text={`RIS-Website öffnen → OGH-Urteil suchen`} />
                  <Step n={2} text="Urteilstext kopieren (oder PDF herunterladen)" />
                  <Step n={3} text="Titel eingeben (z.B. OGH 5Ob200/24)" />
                  <Step n={4} text="Text einfügen oder PDF hochladen → Einlesen" />
                  <Step n={5} text="Ihr Bot-Assistent kennt dieses Urteil ab sofort!" />
                </>
              )}
            </ol>

            {!isCeo && (
              <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/20 rounded-lg">
                <p className="text-xs text-blue-300">
                  <strong>Tipp:</strong> Auch per Matrix-Chat möglich – einfach schreiben:<br />
                  <code className="text-blue-200 bg-blue-900/30 px-2 py-0.5 rounded mt-1 block">/speichern [Titel] | [Text]</code>
                </p>
              </div>
            )}
          </div>

          {/* Upload History */}
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-6">
            <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              Letzte Uploads
            </h3>
            {history.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-4">Noch keine Uploads</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map(entry => (
                  <div key={entry.id} className="flex items-center gap-3 p-3 bg-slate-800/40 rounded-lg">
                    {entry.status === 'ok'
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      : <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{entry.title}</p>
                      <p className="text-slate-500 text-xs">
                        {entry.collection} · {entry.chunks ? `${entry.chunks} Chunks` : ''} · {entry.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Step: React.FC<{ n: number; text: string }> = ({ n, text }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
    <span className="text-slate-400 text-sm">{text}</span>
  </li>
);

export default BrainUpload;
